import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sun, ClipboardCheck, LogOut, HardHat, ClipboardList } from "lucide-react";
import { useAuth } from "../lib/auth";
import SyncPill from "./SyncPill";
import HelpSystem from "./HelpSystem";

/**
 * FieldShell — mobile-first chrome for crew_member users.
 *
 * Intentionally minimal: no signals, no audit, no admin density. Top bar +
 * fixed bottom tab bar. Touch-native targets (>= 48px). Dark commercial tone.
 */
export default function FieldShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || user?.email || "?")
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join("");

  return (
    <div className="flex flex-col min-h-screen" data-testid="field-shell" style={{ background: "var(--bg)" }}>
      {/* Top bar — black, anchored */}
      <header
        className="sticky top-0 z-40 flex items-center gap-3 px-4"
        style={{
          background: "var(--bg-nav)",
          color: "#fff",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          height: 56,
        }}
        data-testid="field-topbar"
      >
        <img src="/brand/mr-mark-512.png" alt="" aria-hidden="true"
             style={{ width: 26, height: 26, objectFit: "contain", flexShrink: 0 }}
             data-testid="brand-mark" />
        <div className="min-w-0 leading-tight flex-1">
          <div className="text-[12px] font-extrabold uppercase tracking-[0.06em] truncate">
            Manchester Roofing
          </div>
          <div className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/45">
            Field Operations
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SyncPill />
          <div className="hidden xs:flex items-center gap-2 px-2 py-1 rounded-md"
               style={{ background: "rgba(255,255,255,0.06)" }} data-testid="field-user">
            <div className="w-7 h-7 rounded-md flex items-center justify-center text-[10.5px] font-bold"
                 style={{ background: "var(--accent)", color: "#fff" }}>{initials}</div>
            <div className="text-[11px] font-semibold truncate max-w-[120px]">{user?.name || user?.email}</div>
          </div>
          <button
            data-testid="field-logout-btn"
            onClick={async () => { await logout(); nav("/login"); }}
            className="p-2 rounded-md text-white/70 hover:text-white hover:bg-white/[0.08]"
            aria-label="Sign out"
            title="Sign out"
            style={{ minWidth: 40, minHeight: 40 }}
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 overflow-y-auto" style={{ paddingBottom: 76 }}>
        <Outlet />
      </main>

      {/* Help — same floating button as the office shell, sits above the bottom nav. */}
      <HelpSystem />

      {/* Bottom nav — touch-native */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-3"
        style={{
          background: "var(--bg-nav)",
          borderTop: "1px solid rgba(255,255,255,0.08)",
          height: 64,
        }}
        data-testid="field-bottom-nav"
      >
        <FieldTab to="/" icon={Sun} label="My Day" testId="nav-my-day" end />
        <FieldTab to="/jobs" icon={ClipboardCheck} label="Jobs" testId="nav-jobs" />
        <FieldTab to="/surveys" icon={ClipboardList} label="Site visits" testId="nav-surveys" />
      </nav>
    </div>
  );
}

function FieldTab({ to, icon: Icon, label, testId, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        "flex flex-col items-center justify-center gap-1 text-[10.5px] font-bold uppercase tracking-[0.14em] " +
        (isActive ? "text-white" : "text-white/55")
      }
    >
      {({ isActive }) => (
        <>
          <Icon size={18} style={{ color: isActive ? "var(--accent)" : "rgba(255,255,255,0.5)" }} />
          <span>{label}</span>
        </>
      )}
    </NavLink>
  );
}

export { HardHat };
